/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import {
  AxiosRequestConfig,
  AxiosResponse,
  RawAxiosRequestHeaders,
} from 'axios';
import { CoreNetworking, type ApiClient } from '@airasia-rn/ui/core-networking';
import type { AppEnvironment } from '../types';

export class BaseApiService {
  protected readonly baseClient: ApiClient;

  constructor(
    environment: AppEnvironment = 'stg',
    baseURLs: { stg: string; prd: string },
  ) {
    this.baseClient = CoreNetworking({
      baseUrl: this.getHost(environment, baseURLs),
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json, text/plain, */*',
      },
      paramsSerializer: {
        indexes: null, // this will serialize arrays as repeat keys: star_rating=one&star_rating=two
      },
    });
  }

  private getHost(
    environment: AppEnvironment,
    baseURLs: { stg: string; prd: string },
  ): string {
    return environment === 'stg' ? baseURLs.stg : baseURLs.prd;
  }

  protected async get<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
    config?: AxiosRequestConfig,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.baseClient.get(url, {
      params,
      headers,
      ...config,
    });
    return response.data;
  }

  protected async post<T>(
    url: string,
    data?: unknown,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.baseClient.post(url, data, {
      params,
      headers,
    });
    return response.data;
  }

  protected async patch<T>(
    url: string,
    data?: unknown,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.baseClient.patch(url, data, {
      params,
      headers,
    });
    return response.data;
  }

  protected async delete<T>(
    url: string,
    params?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.baseClient.delete(url, {
      params,
      headers,
    });
    return response.data;
  }

  protected async deleteWithData<T>(
    url: string,
    data?: unknown,
    headers?: RawAxiosRequestHeaders,
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.baseClient.delete(url, {
      data,
      headers,
    });
    return response.data;
  }
}
